import { LocationPageInterface, LocationVarKey, LocationVarWithId, MergedLocationVar } from "@swivl-mono/types";
import { useState } from "react";

import { WorksheetComponentSwitch } from "../../../../Components/Worksheets/Components/WorksheetComponent/WorksheetComponent.Switch";
import { WorksheetCondition } from "../../../../Components/Worksheets/Types/Worksheet.Interface";
import { classNameForRow, useVisibility } from "../../../../Hooks/WorksheetRow.Hooks";
import { useMergedLocationVariables } from "../../../../Hooks/useLocationVariables";
import LocationVarsModel from "../../../../Models/Variables/LocationVars.Model";

interface InactiveStatusSwitchRowProps extends LocationPageInterface {
  status: string;
  label: string;
  tooltip?: string;
  hideIf?: WorksheetCondition;
}

// Renders a switch toggle that adds or removes `status` from the comma-separated
// `SequencePastDueProcessInactiveStatuses` location variable. Delegates rendering to
// `WorksheetComponentSwitch` (the inner primitive of `SwitchRow`) so the row inherits the
// standard Worksheet UX — inheritance dot, "Customize" overlay on inherited rows, revert/label
// styling, loading state — while persistence stays list-aware here. Co-located with
// `Cortex.SequencesPastDue.tsx` because the variable shape is specific to the Past Due opt-in
// flow (SSMCloud only).
export function InactiveStatusSwitchRow(props: InactiveStatusSwitchRowProps) {
  const { status, label, tooltip, groupId, locationId, type, isEditing } = props;
  const vars = useMergedLocationVariables([LocationVarKey.SequencePastDueProcessInactiveStatuses], groupId, locationId);
  const variable = vars?.[LocationVarKey.SequencePastDueProcessInactiveStatuses];
  const visibility = useVisibility(props);
  const [isLoading, setIsLoading] = useState(false);

  if (!variable) {
    return null;
  }

  const selected = parseStatusList(variable.value);
  const isOn = selected.includes(status);

  // Project the real (comma-separated) variable onto a boolean-shaped variable so
  // `WorksheetComponentSwitch` can render it as a plain on/off toggle. We keep the real
  // metadata (id, key, inheritance, source) so the inner switch's inheritance/Customize UX
  // works exactly like a regular SwitchRow.
  const projectedVariable: MergedLocationVar = {
    id: variable.id,
    key: variable.key,
    value: isOn ? "1" : "0",
    source: variable.source,
    inheritance: variable.inheritance
  };

  const saveVariable = async (value: string): Promise<void> => {
    if (!variable.id) {
      return;
    }
    const nextSelected = value === "1" ? addStatus(selected, status) : removeStatus(selected, status);
    setIsLoading(true);
    try {
      await LocationVarsModel.saveVariable(variable.id, nextSelected.join(","));
    } finally {
      setIsLoading(false);
    }
  };

  // Matches `WorksheetComponent.Component.tsx` customize() semantics: create the override at
  // this scope carrying the parent's current value forward, without toggling. The user clicks
  // again on the now-editable switch to actually flip the status on/off.
  const customize = async (): Promise<void> => {
    setIsLoading(true);
    try {
      await LocationVarsModel.createVariable(LocationVarKey.SequencePastDueProcessInactiveStatuses, variable.value, groupId, locationId);
    } finally {
      setIsLoading(false);
    }
  };

  // The CSV variable is shared by both Auction and Overlock rows, so a real `deleteVariable` would
  // wipe both customizations at this scope. We redefine revert per-row: save the CSV with this
  // row's status removed, leaving the other row untouched. When removing this row's status leaves
  // the CSV empty, fall back to the standard `deleteVariable` so the row genuinely reverts to
  // inherited (and the inheritance dot updates accordingly).
  const deleteVariable = async (): Promise<void> => {
    if (!variable.id) {
      return;
    }
    const nextSelected = removeStatus(selected, status);
    setIsLoading(true);
    try {
      if (nextSelected.length === 0) {
        LocationVarsModel.deleteVariable(variable as LocationVarWithId);
      } else {
        await LocationVarsModel.saveVariable(variable.id, nextSelected.join(","));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <WorksheetComponentSwitch
      className={classNameForRow("SwitchRow", props, visibility)}
      variable={projectedVariable}
      label={label}
      tooltip={tooltip}
      type={type}
      isEditing={isEditing}
      isLoading={isLoading}
      saveVariable={saveVariable}
      customize={customize}
      deleteVariable={deleteVariable}
    />
  );
}

function parseStatusList(raw: string | undefined): string[] {
  if (!raw) {
    return [];
  }
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function addStatus(current: string[], status: string): string[] {
  if (current.includes(status)) {
    return current;
  }
  return [...current, status];
}

function removeStatus(current: string[], status: string): string[] {
  return current.filter((s) => s !== status);
}
